/**
 * Accessories handed over WITH an asset.
 *
 * Two ways to track a charger, and this file is one of them:
 *
 *  1. **As its own asset**, with its own `AST-` code. Right for a docking station or
 *     a second monitor - something valuable, serialised, and worth a history of its
 *     own. Nothing here is involved; it is just an asset in the Accessories category.
 *
 *  2. **As a checklist on the assignment** - this file. Right for the charger, the
 *     bag and the mouse: items nobody will sticker, that always move with the laptop,
 *     and whose only interesting question is "did it come back?".
 *
 * Choosing (2) for cheap items is deliberate. Registering 200 chargers as assets
 * produces a register that is 80% chargers, each needing its own assign and return,
 * and it stops being maintained within a month. A checklist costs one tick at
 * handover and answers the only question anyone actually asks.
 *
 * The list is a SUGGESTION, not an enum. Accessories are not persisted as enum values
 * anywhere - they are free text - so a company with a "SIM card" or a "stylus" adds it
 * without a migration. These are only what the checkbox list offers by default.
 */
export const COMMON_ACCESSORIES = [
  'Charger',
  'Carry bag',
  'Mouse',
  'Keyboard',
  'Docking station',
  'Headset',
  'Webcam',
  'Cable',
] as const;

/** Free text, so this bounds what a single entry may be rather than which it is. */
export const MAX_ACCESSORY_LENGTH = 60;
export const MAX_ACCESSORIES_PER_ASSIGNMENT = 20;

/**
 * Normalise for comparison, so "Charger" ticked at handover matches "charger " ticked
 * at return. Without this the return check reports a charger missing because somebody
 * typed it in a different case.
 */
export function accessoryKey(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Trimmed, de-duplicated, order preserved. What gets stored. */
export function normalizeAccessories(names: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of names) {
    const name = raw.trim();
    if (name === '') continue;
    const key = accessoryKey(name);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name.slice(0, MAX_ACCESSORY_LENGTH));
  }
  return out.slice(0, MAX_ACCESSORIES_PER_ASSIGNMENT);
}

/**
 * What went out but did not come back.
 *
 * The whole point of the checklist. Compared on the normalised key so case and
 * spacing cannot manufacture a false "missing", and the ISSUED spelling is what is
 * reported - that is the one the employee signed for.
 */
export function missingAccessories(
  issued: readonly string[],
  returned: readonly string[],
): string[] {
  const back = new Set(returned.map(accessoryKey));
  return issued.filter((name) => !back.has(accessoryKey(name)));
}

/**
 * Anything handed back that was never issued.
 *
 * Not an error and not hidden: people do return a charger that was never recorded,
 * and silently dropping it would leave the register short by one charger with nobody
 * able to explain where it came from.
 */
export function unexpectedAccessories(
  issued: readonly string[],
  returned: readonly string[],
): string[] {
  const out = new Set(issued.map(accessoryKey));
  return returned.filter((name) => !out.has(accessoryKey(name)));
}
